import React from "react";
import { 
  Filter, Tag, Award, Star, Trophy, Shield, Crown, Heart, Flag, Zap, Circle,
  Square, Diamond, Hexagon, Users, User, Megaphone, Store, Calendar,
  Package, Layers, Building2, Mic, Briefcase, Gift, Gem
} from "lucide-react";
import { CategoryWithCount } from "@portal/public_event/types/exhibitor.types";

interface CategoryFiltersProps {
  categories: CategoryWithCount[] | string[]; // Support both for backward compatibility
  selectedCategories: string[];
  onChange: (categories: string[]) => void;
  primaryColor?: string;
}

// Icon mapping matching the backend
const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  // Event-specific icons
  'users': Users,
  'user': User,
  'mic': Mic,
  'megaphone': Megaphone,
  'store': Store,
  'building2': Building2,
  'briefcase': Briefcase,
  'calendar': Calendar,
  'package': Package,
  'gift': Gift,
  
  // Status/tier icons
  'gem': Gem,
  'trophy': Trophy,
  'award': Award,
  'star': Star,
  'crown': Crown,
  'shield': Shield,
  
  // Generic icons
  'tag': Tag,
  'flag': Flag,
  'heart': Heart,
  'zap': Zap,
  'layers': Layers,
  'circle': Circle,
  'square': Square,
  'diamond': Diamond,
  'hexagon': Hexagon,
};

export const CategoryFilters = ({
  categories,
  selectedCategories,
  onChange,
  primaryColor = "#2563eb" // Default to blue-600
}: CategoryFiltersProps) => {
  // Return early if no categories available
  if (!categories || categories.length === 0) {
    return null;
  }

  // Handle legacy string array format
  const isLegacyFormat = typeof categories[0] === 'string';


  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-gray-600">
        <Filter size={18} />
        <span className="text-sm font-medium">Filter by Category</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {categories.map((category) => {
          const categoryId = isLegacyFormat ? category as string : (category as CategoryWithCount).id;
          const categoryName = isLegacyFormat ? category as string : (category as CategoryWithCount).name;
          const isSelected = selectedCategories.includes(categoryId);
          
          if (isLegacyFormat) {
            // Legacy format - simple button
            return (
              <button
                key={categoryId}
                onClick={() =>
                  onChange(
                    isSelected
                      ? selectedCategories.filter(c => c !== categoryId)
                      : [...selectedCategories, categoryId]
                  )
                }
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  isSelected
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {categoryName}
              </button>
            );
          }
          
          // New format with icon support
          const categoryObj = category as CategoryWithCount;
          const IconComponent = categoryObj.displayProperties?.icon 
            ? ICON_MAP[categoryObj.displayProperties.icon] || Tag 
            : Tag;
          
          return (
            <button
              key={categoryId}
              onClick={() =>
                onChange(
                  isSelected
                    ? selectedCategories.filter(c => c !== categoryId)
                    : [...selectedCategories, categoryId]
                )
              }
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                isSelected 
                  ? 'ring-1 ring-offset-1 ring-blue-500 transform scale-105' 
                  : 'hover:transform hover:scale-105'
              }`}
              style={
                isSelected && categoryObj.displayProperties?.color
                  ? {
                      backgroundColor: categoryObj.displayProperties.badgeStyle === 'solid' 
                        ? categoryObj.displayProperties.color 
                        : `${categoryObj.displayProperties.color}20`,
                      color: categoryObj.displayProperties.badgeStyle === 'solid' 
                        ? categoryObj.displayProperties.textColor || '#ffffff'
                        : categoryObj.displayProperties.color,
                      border: categoryObj.displayProperties.badgeStyle === 'outline' 
                        ? `1px solid ${categoryObj.displayProperties.color}` 
                        : 'none'
                    }
                  : !isSelected 
                    ? { backgroundColor: '#f3f4f6', color: '#4b5563' }
                    : { backgroundColor: '#dbeafe', color: '#2563eb' }
              }
            >
              <IconComponent size={14} className="flex-shrink-0" />
              {categoryName}
              {categoryObj.exhibitorCount > 0 && (
                <span className="ml-1 opacity-75">({categoryObj.exhibitorCount})</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};